import React, { useEffect, useMemo, useState } from "react";
import RichTextEditor from "../../components/RichTextEditor";
import {
  ThemeViewFile,
  getThemeViewFile,
  listThemeViewFiles,
  saveThemeViewFile,
} from "../../services/themeFiles";
import { API_BASE_URL, BASE_URL } from "../../config";
import { getToken } from "../../lib/tokenStorage";

type EditorMode = "code" | "visual";
type PhpTokenMap = Record<string, string>;

function formatDateTime(value?: string) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function buildVisualContent(raw: string): {
  visual: string;
  tokenMap: PhpTokenMap;
  hasPhp: boolean;
} {
  const tokenMap: PhpTokenMap = {};
  let index = 0;

  const visual = raw.replace(/<\?[\s\S]*?\?>/g, (match) => {
    index += 1;
    const token = `PHP_BLOCK_${index}`;
    tokenMap[token] = match;
    return `<span contenteditable="false" data-php-token="${token}" style="display:inline-block;padding:2px 8px;margin:0 2px;border:1px solid #d0d7de;border-radius:6px;background:#f6f8fa;color:#57606a;font-family:monospace;font-size:12px;">[[${token}]]</span>`;
  });

  return {
    visual,
    tokenMap,
    hasPhp: index > 0,
  };
}

function restorePhpBlocks(visual: string, tokenMap: PhpTokenMap): string {
  const withMarkers = visual.replace(
    /<span[^>]*data-php-token="(PHP_BLOCK_\d+)"[^>]*>[\s\S]*?<\/span>/g,
    (_, token: string) => `[[${token}]]`,
  );

  return withMarkers.replace(/\[\[(PHP_BLOCK_\d+)\]\]/g, (full, token) => {
    return tokenMap[token] ?? full;
  });
}

function extractImageSources(raw: string): string[] {
  const list: string[] = [];
  const found = new Set<string>();
  const regex = /<img\b[^>]*\bsrc\s*=\s*["']([^"']+)["'][^>]*>/gi;
  let match: RegExpExecArray | null = regex.exec(raw);

  while (match) {
    const src = (match[1] || "").trim();
    if (src && !found.has(src)) {
      found.add(src);
      list.push(src);
    }
    match = regex.exec(raw);
  }

  return list;
}

function extractBackgroundSources(raw: string): string[] {
  const list: string[] = [];
  const found = new Set<string>();
  const regex = /background(?:-image)?\s*:\s*url\(\s*["']?([^"')]+)["']?\s*\)/gi;
  let match: RegExpExecArray | null = regex.exec(raw);

  while (match) {
    const src = (match[1] || "").trim();
    if (src && !found.has(src)) {
      found.add(src);
      list.push(src);
    }
    match = regex.exec(raw);
  }

  return list;
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function resolveImagePreviewUrl(src: string): string {
  if (/^https?:\/\//i.test(src) || src.startsWith("data:")) {
    return src;
  }

  if (src.startsWith("/")) {
    if (typeof window !== "undefined") {
      return `${window.location.origin}${src}`;
    }
    return `${BASE_URL.replace(/\/$/, "")}${src}`;
  }

  return `${BASE_URL.replace(/\/$/, "")}/${src.replace(/^\//, "")}`;
}

export default function ThemeFiles() {
  const [files, setFiles] = useState<ThemeViewFile[]>([]);
  const [selectedFile, setSelectedFile] = useState("");
  const [content, setContent] = useState("");
  const [visualContent, setVisualContent] = useState("");
  const [phpTokenMap, setPhpTokenMap] = useState<PhpTokenMap>({});
  const [hasPhpBlocks, setHasPhpBlocks] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>("code");
  const [initialContent, setInitialContent] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string>("");

  const [loadingFiles, setLoadingFiles] = useState(true);
  const [loadingFileContent, setLoadingFileContent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [replacingImageSrc, setReplacingImageSrc] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const hasUnsavedChanges = useMemo(
    () => content !== initialContent,
    [content, initialContent],
  );
  const imageSources = useMemo(() => extractImageSources(content), [content]);
  const backgroundSources = useMemo(
    () => extractBackgroundSources(content),
    [content],
  );

  const loadFileList = async () => {
    try {
      setLoadingFiles(true);
      setError(null);

      const response = await listThemeViewFiles();
      if (!response.success) {
        throw new Error(response.message || "Failed to load files");
      }

      const fileList = response.files || [];
      setFiles(fileList);

      if (!selectedFile && fileList.length > 0) {
        setSelectedFile(fileList[0].name);
      } else if (
        selectedFile &&
        !fileList.some((file) => file.name === selectedFile)
      ) {
        setSelectedFile(fileList.length > 0 ? fileList[0].name : "");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load files");
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const loadFileContent = async (fileName: string) => {
    if (!fileName) return;

    try {
      setLoadingFileContent(true);
      setError(null);
      setSuccess(null);

      const response = await getThemeViewFile(fileName);
      if (!response.success) {
        throw new Error(response.message || "Failed to load file");
      }

      const nextContent = response.content || "";
      const visualData = buildVisualContent(nextContent);

      setContent(nextContent);
      setVisualContent(visualData.visual);
      setPhpTokenMap(visualData.tokenMap);
      setHasPhpBlocks(visualData.hasPhp);
      setInitialContent(nextContent);
      setUpdatedAt(response.updated_at || "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load file");
      setContent("");
      setVisualContent("");
      setPhpTokenMap({});
      setHasPhpBlocks(false);
      setInitialContent("");
      setUpdatedAt("");
    } finally {
      setLoadingFileContent(false);
    }
  };

  useEffect(() => {
    loadFileList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedFile) {
      loadFileContent(selectedFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile]);

  const handleFileChange = (nextFile: string) => {
    if (!nextFile || nextFile === selectedFile) return;

    if (
      hasUnsavedChanges &&
      !window.confirm("You have unsaved changes. Discard them and switch file?")
    ) {
      return;
    }

    setSelectedFile(nextFile);
  };

  const handleModeSwitch = (nextMode: EditorMode) => {
    if (nextMode === editorMode) return;

    if (nextMode === "visual") {
      const visualData = buildVisualContent(content);
      setVisualContent(visualData.visual);
      setPhpTokenMap(visualData.tokenMap);
      setHasPhpBlocks(visualData.hasPhp);
      setEditorMode("visual");
      return;
    }

    const restored = restorePhpBlocks(visualContent, phpTokenMap);
    setContent(restored);
    setEditorMode("code");
  };

  const handleVisualChange = (nextVisual: string) => {
    setVisualContent(nextVisual);
    setContent(restorePhpBlocks(nextVisual, phpTokenMap));
  };

  const handleSave = async () => {
    if (!selectedFile) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const saveContent =
        editorMode === "visual"
          ? restorePhpBlocks(visualContent, phpTokenMap)
          : content;

      const response = await saveThemeViewFile(selectedFile, saveContent);
      if (!response.success) {
        throw new Error(response.message || "Failed to save file");
      }

      setContent(saveContent);
      setInitialContent(saveContent);
      setUpdatedAt(response.updated_at || "");
      setSuccess(`Saved ${selectedFile} successfully.`);
      await loadFileList();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save file");
    } finally {
      setSaving(false);
    }
  };

  const replaceImageSourceInContent = (oldSrc: string, newSrc: string) => {
    if (!oldSrc || !newSrc || oldSrc === newSrc) return;

    const nextContent = content.split(oldSrc).join(newSrc);
    setContent(nextContent);

    if (editorMode === "visual") {
      const visualData = buildVisualContent(nextContent);
      setVisualContent(visualData.visual);
      setPhpTokenMap(visualData.tokenMap);
      setHasPhpBlocks(visualData.hasPhp);
    }
  };

  const replaceBackgroundSourceInContent = (oldSrc: string, newSrc: string) => {
    if (!oldSrc || !newSrc || oldSrc === newSrc) return;

    const escaped = escapeRegex(oldSrc);
    const bgRegex = new RegExp(
      `(background(?:-image)?\\s*:\\s*url\\(\\s*["']?)${escaped}(["']?\\s*\\))`,
      "gi",
    );
    const nextContent = content.replace(bgRegex, `$1${newSrc}$2`);
    setContent(nextContent);

    if (editorMode === "visual") {
      const visualData = buildVisualContent(nextContent);
      setVisualContent(visualData.visual);
      setPhpTokenMap(visualData.tokenMap);
      setHasPhpBlocks(visualData.hasPhp);
    }
  };

  const uploadReplacementImage = async (
    oldSrc: string,
    file: File | undefined,
  ) => {
    if (!file) return;

    try {
      setReplacingImageSrc(oldSrc);
      setError(null);
      setSuccess(null);

      const token = getToken();
      if (!token) {
        throw new Error("Not authenticated. Please log in again.");
      }

      const formData = new FormData();
      formData.append("upload", file);

      const response = await fetch(`${API_BASE_URL}/upload.php`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const payload = await response.json();
      if (!response.ok || !payload?.success || !payload?.url) {
        throw new Error(payload?.error || "Image upload failed");
      }

      replaceImageSourceInContent(oldSrc, payload.url);
      setSuccess("Image replaced in editor. Save file to persist changes.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to replace image");
    } finally {
      setReplacingImageSrc(null);
    }
  };

  const uploadReplacementBackground = async (
    oldSrc: string,
    file: File | undefined,
  ) => {
    if (!file) return;

    try {
      setReplacingImageSrc(oldSrc);
      setError(null);
      setSuccess(null);

      const token = getToken();
      if (!token) {
        throw new Error("Not authenticated. Please log in again.");
      }

      const formData = new FormData();
      formData.append("upload", file);

      const response = await fetch(`${API_BASE_URL}/upload.php`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const payload = await response.json();
      if (!response.ok || !payload?.success || !payload?.url) {
        throw new Error(payload?.error || "Background upload failed");
      }

      replaceBackgroundSourceInContent(oldSrc, payload.url);
      setSuccess(
        "Background image replaced in editor. Save file to persist changes.",
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to replace background image",
      );
    } finally {
      setReplacingImageSrc(null);
    }
  };


  return (
    <div className="container-fluid">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h2 className="mb-1">Theme Files</h2>
          <p className="text-muted mb-0">
            Edit files from `twentytwentyfive-child/views`.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={loadFileList}
          disabled={loadingFiles || saving}
        >
          Refresh List
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="card">
        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label htmlFor="theme-file-select" className="form-label">
                File
              </label>
              <select
                id="theme-file-select"
                className="form-select"
                value={selectedFile}
                onChange={(e) => handleFileChange(e.target.value)}
                disabled={loadingFiles || files.length === 0 || saving}
              >
                {files.length === 0 && <option value="">No files found</option>}
                {files.map((file) => (
                  <option key={file.name} value={file.name}>
                    {file.name} ({Math.round(file.size / 1024)} KB)
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <div className="d-flex align-items-center gap-2 w-100 justify-content-md-end">
                <div className="btn-group" role="group" aria-label="Editor mode">
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      editorMode === "code"
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={() => handleModeSwitch("code")}
                    disabled={loadingFileContent}
                  >
                    Code
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      editorMode === "visual"
                        ? "btn-primary"
                        : "btn-outline-secondary"
                    }`}
                    onClick={() => handleModeSwitch("visual")}
                    disabled={loadingFileContent}
                  >
                    WYSIWYG
                  </button>
                </div>
                <div className="text-muted small">
                  Last updated: {formatDateTime(updatedAt)}
                </div>
              </div>
            </div>
          </div>

          <label className="form-label">
            Editor
          </label>

          {editorMode === "visual" && hasPhpBlocks && (
            <div className="alert alert-warning">
              This file contains PHP blocks. In WYSIWYG they are shown as locked
              placeholders. Edit PHP logic in Code mode.
            </div>
          )}

          {editorMode === "visual" ? (
            <RichTextEditor
              value={visualContent}
              onChange={handleVisualChange}
              rows={16}
              placeholder="Edit visual content and insert images from the toolbar..."
            />
          ) : (
            <textarea
              id="theme-file-editor"
              className="form-control font-monospace"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={26}
              spellCheck={false}
              disabled={loadingFileContent || !selectedFile}
              placeholder={
                loadingFiles
                  ? "Loading files..."
                  : "Select a file to start editing..."
              }
            />
          )}

          <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <label className="form-label mb-0">Images in this file</label>
              <small className="text-muted">
                Upload replacement and then click Save File.
              </small>
            </div>

            {imageSources.length === 0 ? (
              <div className="alert alert-light border mb-0">
                No image tags detected in the current content.
              </div>
            ) : (
              <div className="border rounded p-3">
                <div className="row g-3">
                  {imageSources.map((src) => (
                    <div className="col-12" key={src}>
                      <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center">
                        <img
                          src={resolveImagePreviewUrl(src)}
                          alt="Current file asset"
                          style={{
                            width: "96px",
                            height: "96px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #dee2e6",
                          }}
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                        <div className="flex-grow-1">
                          <div className="small text-muted mb-1">Current src</div>
                          <code
                            style={{
                              display: "inline-block",
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {src}
                          </code>
                        </div>
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            className="form-control form-control-sm"
                            disabled={replacingImageSrc === src}
                            onChange={(e) =>
                              uploadReplacementImage(src, e.target.files?.[0])
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-4">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <label className="form-label mb-0">Background URLs in this file</label>
              <small className="text-muted">
                Detected from `background` / `background-image`.
              </small>
            </div>

            {backgroundSources.length === 0 ? (
              <div className="alert alert-light border mb-0">
                No background URLs detected in the current content.
              </div>
            ) : (
              <div className="border rounded p-3">
                <div className="row g-3">
                  {backgroundSources.map((src) => (
                    <div className="col-12" key={`bg-${src}`}>
                      <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center">
                        <img
                          src={resolveImagePreviewUrl(src)}
                          alt="Current background asset"
                          style={{
                            width: "96px",
                            height: "96px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #dee2e6",
                          }}
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                        <div className="flex-grow-1">
                          <div className="small text-muted mb-1">
                            Current background URL
                          </div>
                          <code
                            style={{
                              display: "inline-block",
                              maxWidth: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {src}
                          </code>
                        </div>
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            className="form-control form-control-sm"
                            disabled={replacingImageSrc === src}
                            onChange={(e) =>
                              uploadReplacementBackground(src, e.target.files?.[0])
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center justify-content-between mt-3">
            <div className="text-muted small">
              {loadingFileContent
                ? "Loading file content..."
                : hasUnsavedChanges
                  ? "Unsaved changes"
                  : "All changes saved"}
            </div>

            <button
              type="button"
              className="btn btn-primary"
              disabled={!selectedFile || !hasUnsavedChanges || saving}
              onClick={handleSave}
            >
              {saving ? "Saving..." : "Save File"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
